import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Layers, Search, CheckCircle, Archive } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700",
  pending: "bg-amber-100 text-amber-800",
  proposed: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  published: "bg-emerald-100 text-emerald-800",
  archived: "bg-red-100 text-red-700",
  retired: "bg-red-100 text-red-700",
};

export default function ComponentLibrary() {
  const [components, setComponents] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ display_name: "", component_type_id: "", construction_notes: "", applicable_categories: "" });
  const [saving, setSaving] = useState(false);
  const [keyword, setKeyword] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    params.set("include_retired", "true");
    Promise.all([
      api.get(`/garment-components?${params.toString()}`).then((r) => {
        setComponents(r.data);
      }),
      api.get("/component-types").then((r) => {
        setTypes(r.data);
      }),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/garment-components", {
        display_name: form.display_name,
        component_type_id: form.component_type_id,
        construction_notes: form.construction_notes,
        applicable_categories: form.applicable_categories ? form.applicable_categories.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("Component proposed");
      setShowCreate(false);
      setForm({ display_name: "", component_type_id: "", construction_notes: "", applicable_categories: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const publishComponent = async (id) => {
    try {
      await api.post(`/garment-components/${id}/publish`, { published_by: "admin" });
      toast.success("Component published");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const retireComponent = async (id) => {
    try {
      await api.delete(`/garment-components/${id}`);
      toast.success("Component retired");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const typeNameMap = Object.fromEntries(types.map((t) => [t.id, t.name]));

  const filtered = components.filter((c) => {
    if (selectedType !== "all" && c.component_type_id !== selectedType) return false;
    if (keyword && !c.display_name.toLowerCase().includes(keyword.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Component Library</h1>
          <p className="text-zinc-500 mt-2 text-sm">Manage reusable components: trims, labels, buttons, zippers and more.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> Propose component
        </button>
      </div>

      {/* Keyword Search */}
      <div className="mt-6 relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Search components by name..."
          className="w-full border border-zinc-200 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : (
        <div className="mt-8 flex gap-6">
          {/* Type Sidebar */}
          <div className="w-48 shrink-0">
            <h3 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3">Types</h3>
            <div className="space-y-1">
              <button onClick={() => setSelectedType("all")}
                className={`w-full text-left px-3 py-2 rounded-md text-sm ${selectedType === "all" ? "bg-zinc-100 font-medium" : "text-zinc-600 hover:bg-zinc-50"}`}>
                All ({components.length})
              </button>
              {types.map((t) => (
                <button key={t.id} onClick={() => setSelectedType(t.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm ${selectedType === t.id ? "bg-zinc-100 font-medium" : "text-zinc-600 hover:bg-zinc-50"}`}>
                  {t.name} ({components.filter((c) => c.component_type_id === t.id).length})
                </button>
              ))}
            </div>

            <h3 className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3 mt-8">Status</h3>
            <div className="space-y-1">
              {["all", "draft", "pending", "published", "retired"].map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm capitalize ${statusFilter === s ? "bg-zinc-100 font-medium" : "text-zinc-600 hover:bg-zinc-50"}`}>
                  {s === "all" ? "All statuses" : s}
                </button>
              ))}
            </div>
          </div>

          {/* Components Grid */}
          <div className="flex-1">
            {filtered.length === 0 ? (
              <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
                <Layers size={26} className="mx-auto mb-3" />
                <p className="text-sm">No components match your filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((c) => (
                  <div key={c.id} className="border border-zinc-200 bg-white rounded-lg p-5 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{typeNameMap[c.component_type_id] || "—"}</p>
                        <p className="text-sm font-medium mt-0.5">{c.display_name}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[c.status] || STATUS_STYLE.draft}`}>
                        {c.status}
                      </span>
                    </div>
                    {c.construction_notes && <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{c.construction_notes}</p>}
                    {(c.applicable_categories || []).length > 0 && (
                      <p className="text-xs text-zinc-400 mt-2 border-t border-zinc-100 pt-2">{c.applicable_categories.join(", ")}</p>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-zinc-100">
                      <span className="text-[10px] text-zinc-400">Usage: {c.usage_count || 0}</span>
                      <div className="flex gap-1">
                        {(c.status === "pending" || c.status === "draft") && (
                          <button onClick={() => publishComponent(c.id)}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100" title="Publish (admin)">
                            <CheckCircle size={10} /> Publish
                          </button>
                        )}
                        {c.status === "published" && (
                          <button onClick={() => retireComponent(c.id)}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100" title="Retire (admin)">
                            <Archive size={10} /> Retire
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Propose component</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Component name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={form.component_type_id} onChange={(e) => setForm({ ...form, component_type_id: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select type</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <textarea value={form.construction_notes} onChange={(e) => setForm({ ...form, construction_notes: e.target.value })} rows={2} placeholder="Construction notes"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <textarea value={form.applicable_categories} onChange={(e) => setForm({ ...form, applicable_categories: e.target.value })} rows={3} placeholder="Applicable categories (comma separated)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <button onClick={create} disabled={!form.display_name || !form.component_type_id || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Submitting..." : "Propose component"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
